import React, { useState } from 'react';
import { ArrowBack, UploadFile, CheckCircle } from "@mui/icons-material";
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';

// Mesmo fix de sempre pro ícone padrão do Leaflet não resolver certo com
// CRA/Webpack (bug conhecido do pacote) — precisa aqui porque uma feição
// Point (sem pointToLayer customizado) cai no marker padrão do GeoJSON.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TIPOS: { value: string; label: string }[] = [
  { value: 'bacia', label: 'Bacia' },
  { value: 'rio', label: 'Rio' },
  { value: 'represa', label: 'Represa' },
  { value: 'trecho_municipio', label: 'Trecho por município' },
  { value: 'area_pesca', label: 'Área de pesca específica' },
  { value: 'outro', label: 'Outro' },
];

interface Feicao {
  indice: number;
  nomeSugerido: string | null;
  tipoGeometria: string;
  quantidadeVertices: number;
  geometria: any;
  propriedades: Record<string, any>;
}

type Modo = null | 'existente' | 'novo';

// Ajusta o zoom/centro do mapinha de prévia pro contorno da feição inteiro
// — cada feição tem uma extensão bem diferente (uma represa pequena x o
// contorno de uma bacia inteira), um center/zoom fixo não serviria pra
// nenhuma das duas.
function AjustarAosLimites({ geojsonData }: { geojsonData: any }) {
  const map = useMap();
  React.useEffect(() => {
    const bounds = L.geoJSON(geojsonData).getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [12, 12] });
    }
  }, [geojsonData, map]);
  return null;
}

const ImportarGeometria = () => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroUpload, setErroUpload] = useState('');
  const [feicoes, setFeicoes] = useState<Feicao[]>([]);
  const [associadas, setAssociadas] = useState<Record<number, string>>({});

  const [modoAberto, setModoAberto] = useState<Record<number, Modo>>({});
  const [buscaDestino, setBuscaDestino] = useState<Record<number, string>>({});
  const [resultadosBusca, setResultadosBusca] = useState<Record<number, any[]>>({});
  const [uidDestinoEscolhido, setUidDestinoEscolhido] = useState<Record<number, string>>({});
  const [nomeNovo, setNomeNovo] = useState<Record<number, string>>({});
  const [tipoNovo, setTipoNovo] = useState<Record<number, string>>({});
  const [salvando, setSalvando] = useState<Record<number, boolean>>({});
  const [erroAssociar, setErroAssociar] = useState<Record<number, string>>({});

  const handleImportar = async () => {
    if (!arquivo) return;
    setEnviando(true);
    setErroUpload('');
    setFeicoes([]);
    // Reset de TODO o estado por card, não só de feicoes/associadas — os
    // índices (0, 1, 2...) são só a posição da feição DENTRO de cada
    // importação, então sem isso um nome/tipo digitado numa importação
    // anterior "sobrevive" e vaza pra feição de OUTRO conteúdo que caia no
    // mesmo índice na importação seguinte (bug real: um nome ficou colado
    // na geometria errada dessa forma).
    setAssociadas({});
    setModoAberto({});
    setBuscaDestino({});
    setResultadosBusca({});
    setUidDestinoEscolhido({});
    setNomeNovo({});
    setTipoNovo({});
    setSalvando({});
    setErroAssociar({});
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      // Sem header de Content-Type manual — o axios/browser gera o boundary
      // certo sozinho pra FormData; setar "multipart/form-data" na mão sem
      // boundary é um bug clássico que quebra o parse no multer.
      const res = await api.post('/pesca/locais/importar-geometria', formData);
      setFeicoes(res.data.data);
    } catch (err: any) {
      setErroUpload(err?.response?.data?.message || 'Erro ao importar o arquivo.');
    } finally {
      setEnviando(false);
    }
  };

  const abrirModo = (indice: number, modo: Modo, feicao: Feicao) => {
    setModoAberto((prev) => ({ ...prev, [indice]: prev[indice] === modo ? null : modo }));
    if (modo === 'novo' && !nomeNovo[indice]) {
      setNomeNovo((prev) => ({ ...prev, [indice]: feicao.nomeSugerido || '' }));
      setTipoNovo((prev) => ({ ...prev, [indice]: tipoSugeridoPorGeometria(feicao.tipoGeometria) }));
    }
  };

  const buscarDestino = (indice: number, texto: string) => {
    setBuscaDestino((prev) => ({ ...prev, [indice]: texto }));
    if (texto.trim().length < 2) {
      setResultadosBusca((prev) => ({ ...prev, [indice]: [] }));
      return;
    }
    api.get(`/pesca/locais?search=${encodeURIComponent(texto.trim())}`)
      .then((res) => setResultadosBusca((prev) => ({ ...prev, [indice]: res.data.data })));
  };

  const associarComExistente = async (feicao: Feicao) => {
    const uidDestino = uidDestinoEscolhido[feicao.indice];
    if (!uidDestino) return;
    setSalvando((prev) => ({ ...prev, [feicao.indice]: true }));
    setErroAssociar((prev) => ({ ...prev, [feicao.indice]: '' }));
    try {
      const res = await api.put(`/pesca/locais/${uidDestino}`, { geometria: feicao.geometria });
      setAssociadas((prev) => ({ ...prev, [feicao.indice]: res.data.data.nome }));
    } catch (err: any) {
      setErroAssociar((prev) => ({ ...prev, [feicao.indice]: err?.response?.data?.message || 'Erro ao associar.' }));
    } finally {
      setSalvando((prev) => ({ ...prev, [feicao.indice]: false }));
    }
  };

  const associarComNovo = async (feicao: Feicao) => {
    const nome = nomeNovo[feicao.indice]?.trim();
    const tipo = tipoNovo[feicao.indice];
    if (!nome || !tipo) return;
    setSalvando((prev) => ({ ...prev, [feicao.indice]: true }));
    setErroAssociar((prev) => ({ ...prev, [feicao.indice]: '' }));
    try {
      const res = await api.post('/pesca/locais', { nome, tipo, geometria: feicao.geometria });
      setAssociadas((prev) => ({ ...prev, [feicao.indice]: res.data.data.nome }));
    } catch (err: any) {
      setErroAssociar((prev) => ({ ...prev, [feicao.indice]: err?.response?.data?.message || 'Erro ao criar local.' }));
    } finally {
      setSalvando((prev) => ({ ...prev, [feicao.indice]: false }));
    }
  };

  const pendentes = feicoes.filter((f) => !associadas[f.indice]);
  const feitas = feicoes.length - pendentes.length;

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Importar Geometria</h1>
        <div className={styles.actionsHeader}>
          {feicoes.length > 0 && (
            <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
              <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{feitas}</span> de {feicoes.length} associadas
            </p>
          )}
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-8px', marginBottom: '16px' }}>
        Envie um Shapefile zipado (.zip) ou um GeoJSON (.json/.geojson) com o contorno de bacias, represas ou trechos
        de rio. As geometrias são simplificadas automaticamente. Depois, associe cada feição encontrada a um local já
        cadastrado ou crie um local novo a partir dela — uma de cada vez.
      </p>

      <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={fileLabelStyle}>
          <UploadFile fontSize="small" />
          {arquivo ? arquivo.name : 'Escolher arquivo...'}
          <input
            type="file"
            accept=".zip,.json,.geojson"
            style={{ display: 'none' }}
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
          />
        </label>
        <button style={primaryBtnStyle} disabled={!arquivo || enviando} onClick={handleImportar}>
          {enviando ? 'Importando...' : 'Importar'}
        </button>
        {erroUpload && <p style={{ color: '#f75a68', fontSize: '0.85rem', margin: 0 }}>{erroUpload}</p>}
      </div>

      {feicoes.length === 0 && !enviando && !erroUpload && (
        <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>Nenhum arquivo importado ainda.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {feicoes.map((feicao) => {
          const nomeAssociado = associadas[feicao.indice];
          return (
            <div key={feicao.indice} style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', opacity: nomeAssociado ? 0.6 : 1 }}>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ width: '160px', height: '110px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                  <MapContainer center={[-22.9, -48.4]} zoom={6} style={{ width: '100%', height: '100%' }} zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />
                    <GeoJSON data={feicao.geometria} pathOptions={{ color: '#00b37e', weight: 2, fillOpacity: 0.2 }} />
                    <AjustarAosLimites geojsonData={feicao.geometria} />
                  </MapContainer>
                </div>

                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                    <div>
                      <strong style={{ fontSize: '0.95rem' }}>{feicao.nomeSugerido || `Feição ${feicao.indice + 1} (sem nome no arquivo)`}</strong>
                      <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '2px' }}>
                        {rotuloTipoGeometria(feicao.tipoGeometria)} · {feicao.quantidadeVertices} vértice{feicao.quantidadeVertices > 1 ? 's' : ''}
                      </div>
                    </div>
                    {nomeAssociado ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--green-700, #00b37e)', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        <CheckCircle fontSize="small" /> {nomeAssociado}
                      </span>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button style={smallBtnStyle(modoAberto[feicao.indice] === 'existente')} onClick={() => abrirModo(feicao.indice, 'existente', feicao)}>
                          Associar a local existente
                        </button>
                        <button style={smallBtnStyle(modoAberto[feicao.indice] === 'novo')} onClick={() => abrirModo(feicao.indice, 'novo', feicao)}>
                          Criar novo local
                        </button>
                      </div>
                    )}
                  </div>

                  {!nomeAssociado && modoAberto[feicao.indice] === 'existente' && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                      <input
                        style={inputStyle}
                        placeholder="Buscar local pelo nome..."
                        value={buscaDestino[feicao.indice] || ''}
                        onChange={(e) => buscarDestino(feicao.indice, e.target.value)}
                      />
                      {(resultadosBusca[feicao.indice] || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {resultadosBusca[feicao.indice].map((l: any) => (
                            <button
                              key={l.uid_pesca_local}
                              onClick={() => setUidDestinoEscolhido((prev) => ({ ...prev, [feicao.indice]: l.uid_pesca_local }))}
                              style={chipStyle(uidDestinoEscolhido[feicao.indice] === l.uid_pesca_local)}
                            >
                              {l.nome} <span style={{ opacity: 0.6 }}>({l.tipo}{l.pai ? ` — ${l.pai.nome}` : ''})</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <button
                          style={primaryBtnStyle}
                          disabled={!uidDestinoEscolhido[feicao.indice] || salvando[feicao.indice]}
                          onClick={() => associarComExistente(feicao)}
                        >
                          {salvando[feicao.indice] ? 'Associando...' : 'Confirmar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {!nomeAssociado && modoAberto[feicao.indice] === 'novo' && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 2, minWidth: '160px' }}>
                        <label style={labelStyle}>Nome</label>
                        <input
                          style={inputStyle}
                          value={nomeNovo[feicao.indice] ?? (feicao.nomeSugerido || '')}
                          onChange={(e) => setNomeNovo((prev) => ({ ...prev, [feicao.indice]: e.target.value }))}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <label style={labelStyle}>Tipo</label>
                        <select
                          style={inputStyle}
                          value={tipoNovo[feicao.indice] ?? tipoSugeridoPorGeometria(feicao.tipoGeometria)}
                          onChange={(e) => setTipoNovo((prev) => ({ ...prev, [feicao.indice]: e.target.value }))}
                        >
                          {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <button style={primaryBtnStyle} disabled={salvando[feicao.indice]} onClick={() => associarComNovo(feicao)}>
                        {salvando[feicao.indice] ? 'Criando...' : 'Criar e vincular'}
                      </button>
                    </div>
                  )}

                  {erroAssociar[feicao.indice] && <p style={{ color: '#f75a68', fontSize: '0.82rem', marginTop: '8px' }}>{erroAssociar[feicao.indice]}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function rotuloTipoGeometria(tipo: string): string {
  if (tipo === 'Polygon' || tipo === 'MultiPolygon') return 'Polígono';
  if (tipo === 'LineString' || tipo === 'MultiLineString') return 'Linha';
  return 'Ponto';
}

// Sugestão inicial de tipo de local a partir do formato geométrico —
// bacia/represa e rio empatam em Polygon/LineString, então "bacia"/"rio"
// são só o palpite mais comum; o usuário pode trocar livremente no select.
function tipoSugeridoPorGeometria(tipoGeometria: string): string {
  if (tipoGeometria === 'Polygon' || tipoGeometria === 'MultiPolygon') return 'bacia';
  if (tipoGeometria === 'LineString' || tipoGeometria === 'MultiLineString') return 'rio';
  return 'area_pesca';
}

const fileLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '20px',
  border: '1px solid #ddd', background: '#fafafa', color: '#444', fontSize: '0.85rem', cursor: 'pointer',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: '4px' };
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
};
const smallBtnStyle = (ativo: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
  border: ativo ? '2px solid #03c9fe' : '1px solid #ddd', background: ativo ? '#eaf9ff' : '#fff', color: '#333',
});
const chipStyle = (selecionado: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer',
  border: selecionado ? '2px solid #00b37e' : '1px solid #ddd', background: selecionado ? '#e6fbf3' : '#fafafa', color: '#333',
});

export default ImportarGeometria;
